import { useGlobalSearch } from './features/globalSearch/useGlobalSearch';
import { EntryRoutes } from './routing/EntryRoutes';
import { WorkspaceRoutes } from './routing/WorkspaceRoutes';
import { selectEntryRoute, selectWorkspaceGate } from './routing/guards';
import { RouteLoading } from './routing/RouteContent';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { AppShell, getSidebarChrome, type NavItem, type UserAccountMenuItem } from '@kangaroo/ui';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import type { SupportMasqueradeUserOption, User } from '@kangaroo/shared';
import { hasSupportMasqueradeAccess } from '@kangaroo/shared';

import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import GarageOutlinedIcon from '@mui/icons-material/GarageOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import AppleIcon from '@mui/icons-material/Apple';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';

import { AuthAvatar } from './components/AuthAvatar';
import { FeedbackDialog } from './components/feedback/FeedbackDialog';
import { OnboardingWizardDialog } from './components/OnboardingWizardDialog';

import { WhatsNewDialog } from './components/WhatsNewDialog';
import { UsagePaymentPrompt } from './components/billing/UsagePaymentPrompt';
import {
  fetchCurrentUser,
  fetchSupportMasqueradeOptions,
  startSupportMasquerade,
} from './features/auth';
import {
  currentReleaseNoteKey,
  dismissReleaseNote,
  fetchCurrentReleaseNote,
} from './features/releaseNotes';
import {
  currentTeamEntitlementsKey,
  currentTeamKey,
  fetchCurrentTeam,
  fetchTeamEntitlements,
} from './features/teams';
import { clearSessionForExpectedToken, useAuthStore } from './store/authStore';
import { PublicPrivacyPage } from './pages/PublicPrivacyPage';
import { PublicTermsRedirect } from './pages/PublicTermsRedirect';
import { SettingsDrawer } from './pages/settings/SettingsDrawer';
import { EmailConsentPrompt } from './components/settings/EmailConsentPrompt';
import { parseSettingsSection, type SettingsSection } from './pages/settings/SettingsPage';

import { CircuitDiscoveryProvider, useCircuitDiscovery } from './contexts/CircuitDiscoveryContext';
import { adminNavigation } from './pages/admin/adminNavigation';
import { billingNavItem, legacyBillingDestination } from './lib/billingNavigation';
import { isHistoricalSessionsOnlyAccess } from './lib/billingAccess';
import { workspaceGuide } from './lib/workspaceGuide';
import { HELP_PAGES_ENABLED, LIVE_BETA_ENABLED } from './lib/featureFlags';
import {
  KIOSK_ACCOUNT_IDLE_MS,
  KIOSK_ACCOUNT_WARNING_MS,
  getJwtExpiresAtMs,
  startKioskAccountInactivityWatcher,
} from './lib/kioskAccountInactivity';
import type { KioskClientTerminationReason } from './lib/kioskAccountSession';
import {
  clearDismissedWorkspaceGuidePath,
  readDismissedWorkspaceGuidePath,
  writeDismissedWorkspaceGuidePath,
} from './lib/workspaceGuideDismissal';

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Team',
    icon: <Groups2OutlinedIcon />,
    path: '/drivers',
    tooltip: 'Add drivers, parents, coaches, and mechanics here.',
  },
  {
    label: 'Garage',
    icon: <GarageOutlinedIcon />,
    path: '/karts',
    tooltip: 'Add karts and vehicles, assign drivers, and track readiness.',
  },
  {
    label: 'Circuits',
    icon: <MapOutlinedIcon />,
    path: '/circuits',
    tooltip: 'Review detected tracks and keep your circuit library tidy.',
  },
  {
    label: 'Sessions',
    icon: <FlagOutlinedIcon />,
    path: '/sessions?view=all',
    tooltip: 'Upload and review laps, telemetry, notes, and video.',
    children: [
      { label: 'All sessions', path: '/sessions?view=all' },
      { label: 'Footage', path: '/sessions?view=footage' },
      { label: 'Timings', path: '/sessions?view=timings' },
      { label: 'Recordings', path: '/sessions?view=recordings' },
    ],
  },
  {
    label: 'Leaderboard',
    icon: <EmojiEventsOutlinedIcon />,
    path: '/leaderboard',
    tooltip: 'Compare driver progress once sessions start coming in.',
  },
];

const DEVTOOLS_NAV_ITEM: NavItem = {
  label: 'Devtools',
  icon: <BuildOutlinedIcon />,
  path: '/devtools',
  tooltip: 'Developer tools for upload, track, and performance debugging.',
};
const LIVE_BETA_NAV_ITEM: NavItem = {
  label: 'Live',
  icon: <LiveTvOutlinedIcon />,
  path: '/live',
  tooltip: 'Private beta for live GoPro video and timing.',
};
const IOS_APP_NAV_ITEM: NavItem = {
  label: 'iOS app',
  icon: <AppleIcon />,
  path: 'https://apps.apple.com/sa/app/paddiq/id6785660120',
  external: true,
  dividerBefore: true,
  tooltip: 'Download Paddiq on the App Store.',
};

const NO_TEAM_NAV_ITEMS: NavItem[] = [billingNavItem, IOS_APP_NAV_ITEM];

const BILLING_RECOVERY_NAV_ITEMS: NavItem[] = [
  billingNavItem,
  {
    label: 'Team',
    icon: <Groups2OutlinedIcon />,
    path: '/drivers',
    tooltip: 'Review team access.',
  },
  IOS_APP_NAV_ITEM,
];

const LOGOUT_ONLY_MENU_ITEMS: UserAccountMenuItem[] = [];
const DEFAULT_USER_MENU_ITEMS: UserAccountMenuItem[] = [
  { id: 'profile', label: 'Profile & avatar' },
  { id: 'interface', label: 'Interface' },
  { id: 'measurements', label: 'Measurements' },
  { id: 'password', label: 'Change password' },
  { id: 'connections', label: 'Connected apps' },
];
const SUPPORT_MASQUERADE_MENU_ITEM: UserAccountMenuItem = {
  id: 'support-masquerade',
  label: 'Masquerade as customer',
};
const RESTORE_SUPPORT_SESSION_MENU_ITEM: UserAccountMenuItem = {
  id: 'restore-support-session',
  label: 'Return to support account',
};

function getInitials(user: User | null) {
  if (!user?.name) {
    return 'GL';
  }

  return user.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function formatRole(role: User['role']) {
  return role
    .toLowerCase()
    .split('_')
    .map((part) => part[0]!.toUpperCase() + part.slice(1))
    .join(' ');
}

type CurrentTeamEntitlements = Awaited<ReturnType<typeof fetchTeamEntitlements>>;

function withoutHelpNavItems(items: NavItem[]): NavItem[] {
  if (HELP_PAGES_ENABLED) {
    return items;
  }

  return items.filter((item) => item.path !== '/help');
}

function navItemsForWorkspace(
  user: User | null,
  entitlements: CurrentTeamEntitlements | null | undefined,
): NavItem[] {
  const items = withoutHelpNavItems(NAV_ITEMS).map((item) => {
    if (entitlements?.planId === 'privateer' && item.path === '/drivers') {
      return {
        ...item,
        label: 'Driver',
        tooltip: 'Manage your driver profile and solo workspace.',
      };
    }

    return item;
  });

  items.push(billingNavItem, ...adminNavigation(user));
  if (LIVE_BETA_ENABLED) {
    const sessionsIndex = items.findIndex((item) => item.path.startsWith('/sessions'));
    items.splice(sessionsIndex + 1, 0, LIVE_BETA_NAV_ITEM);
  }
  if (import.meta.env.DEV) {
    items.push(DEVTOOLS_NAV_ITEM);
  }
  items.push(IOS_APP_NAV_ITEM);
  return items;
}

function SidebarUserAvatar({ user }: { user: User }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chrome = getSidebarChrome(isDark);

  return (
    <AuthAvatar
      src={user.avatarUrl}
      alt={user.name}
      sx={{
        width: 32,
        height: 32,
        fontSize: '0.66rem',
        fontWeight: 900,
        bgcolor: chrome.profileAvatarBg,
        color: '#FFFFFF',
        border: `1px solid ${chrome.profileAvatarBorder}`,
      }}
    >
      {getInitials(user)}
    </AuthAvatar>
  );
}

function parseSettingsPath(path: string) {
  if (path === '/settings') {
    return { isSettings: true, section: 'profile' as SettingsSection };
  }

  if (!path.startsWith('/settings?')) {
    return { isSettings: false, section: 'profile' as SettingsSection };
  }

  const query = path.slice('/settings?'.length);
  return {
    isSettings: true,
    section: parseSettingsSection(new URLSearchParams(query).get('section')),
  };
}

interface AuthenticatedShellProps {
  navItems: NavItem[];
  fallbackPath: string;
  children: React.ReactNode;
  teams: Array<{ id: string; name: string }>;
  currentTeamId: string;
  onTeamChange: (teamId: string) => void;
  teamSubtitle?: string;
  user: User;
  userMenuItems?: UserAccountMenuItem[];
  onSupportMasquerade?: () => void;
  onRestoreSupportSession?: () => void;
  onLogout: () => void;
  logoutLabel?: string;
  sessionNotice?: React.ReactNode;
}

function AuthenticatedShell(props: AuthenticatedShellProps) {
  const hasCircuitNav = props.navItems.some((item) => item.path === '/circuits');
  if (hasCircuitNav) {
    return <AuthenticatedShellWithDiscovery {...props} />;
  }

  return <AuthenticatedShellInner {...props} />;
}

function AuthenticatedShellWithDiscovery(props: AuthenticatedShellProps) {
  const { candidates, hasNewTracks, pendingCount, ready } = useCircuitDiscovery();
  const navItems = useMemo(
    () =>
      props.navItems.map((item) => {
        if (item.path !== '/circuits') {
          return item;
        }

        if (hasNewTracks) {
          return { ...item, notificationCount: candidates.length };
        }

        if (!ready && pendingCount > 0) {
          return { ...item, notificationPending: true };
        }

        return item;
      }),
    [candidates.length, hasNewTracks, pendingCount, props.navItems, ready],
  );

  return <AuthenticatedShellInner {...props} navItems={navItems} />;
}

function AuthenticatedShellInner({
  navItems,
  fallbackPath,
  children,
  teams,
  currentTeamId,
  onTeamChange,
  teamSubtitle,
  user,
  userMenuItems,
  onSupportMasquerade,
  onRestoreSupportSession,
  onLogout,
  logoutLabel,
  sessionNotice,
}: AuthenticatedShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('profile');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const { results: globalSearchResults, loading: globalSearchLoading } = useGlobalSearch(globalSearchQuery, currentTeamId);

  function openSettings(section?: string | null) {
    if (section === 'billing') {
      setSettingsOpen(false);
      navigate('/billing');
      return;
    }
    setSettingsSection(parseSettingsSection(section));
    setSettingsOpen(true);
  }

  function handleNavigate(path: string) {
    const settingsTarget = parseSettingsPath(path);
    if (settingsTarget.isSettings) {
      openSettings(settingsTarget.section);
      return;
    }

    navigate(path);
  }

  function handleUserMenuSelect(itemId: string) {
    if (itemId === SUPPORT_MASQUERADE_MENU_ITEM.id) {
      onSupportMasquerade?.();
      return;
    }

    if (itemId === RESTORE_SUPPORT_SESSION_MENU_ITEM.id) {
      onRestoreSupportSession?.();
      return;
    }

    if (itemId === 'help') {
      navigate('/help');
      return;
    }

    openSettings(itemId);
  }

  useEffect(() => {
    const billingDestination = legacyBillingDestination(location.pathname, location.search);
    if (billingDestination) {
      setSettingsOpen(false);
      navigate(billingDestination, { replace: true });
      return;
    }
    if (location.pathname !== '/settings') {
      return;
    }

    openSettings(new URLSearchParams(location.search).get('section'));
    navigate(fallbackPath, { replace: true });
  }, [fallbackPath, location.pathname, location.search, navigate]);

  const shellCurrentPath = settingsOpen ? '/settings' : `${location.pathname}${location.search}`;

  return (
    <AppShell
      navItems={navItems}
      currentPath={shellCurrentPath}
      onNavigate={handleNavigate}
      teams={teams}
      currentTeamId={currentTeamId}
      onTeamChange={onTeamChange}
      teamSubtitle={teamSubtitle}
      userName={user.name}
      userRole={formatRole(user.role)}
      userInitials={getInitials(user)}
      sidebarDriver={{
        name: user.name,
        role: formatRole(user.role),
        initials: getInitials(user),
        avatarUrl: user.avatarUrl,
        avatar: <SidebarUserAvatar user={user} />,
        profilePath: '/settings',
      }}
      onUserMenuSelect={handleUserMenuSelect}
      userMenuItems={userMenuItems}
      onLogout={onLogout}
      logoutLabel={logoutLabel}
      onFeedback={() => setFeedbackOpen(true)}
      feedbackIcon={<FeedbackOutlinedIcon />}
      feedbackLabel="Feedback"
      onHelp={HELP_PAGES_ENABLED ? () => navigate('/help') : undefined}
      onSettings={() => openSettings('interface')}
      globalSearchResults={globalSearchResults}
      globalSearchLoading={globalSearchLoading}
      onGlobalSearchQueryChange={setGlobalSearchQuery}
    >
      {sessionNotice}
      <EmailConsentPrompt onPreferences={() => openSettings('profile')} />
      {children}
      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        section={settingsSection}
        onSectionChange={openSettings}
      />
      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        user={user}
        currentUrl={`${window.location.origin}${location.pathname}${location.search}`}
      />
    </AppShell>
  );
}

function getErrorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? 'Request failed';
  }

  return error instanceof Error ? error.message : 'Unknown error';
}

function blurActiveElement() {
  if (typeof document === 'undefined') {
    return;
  }

  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement) {
    activeElement.blur();
  }
}

interface SupportMasqueradeDialogProps {
  open: boolean;
  onClose: () => void;
  onStarted: (token: string, user: User) => Promise<void>;
}

function SupportMasqueradeDialog({ open, onClose, onStarted }: SupportMasqueradeDialogProps) {
  const [search, setSearch] = useState('');
  const [teamId, setTeamId] = useState('');
  const [selectedUser, setSelectedUser] = useState<SupportMasqueradeUserOption | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data, isLoading } = useSWR(
    open ? ['support-masquerade-options', search, teamId] : null,
    () => fetchSupportMasqueradeOptions({ q: search || undefined, teamId: teamId || undefined }),
  );

  useEffect(() => {
    if (!open) {
      setSearch('');
      setTeamId('');
      setSelectedUser(null);
      setReason('');
      setError(null);
    }
  }, [open]);

  async function handleStart() {
    if (!selectedUser) {
      setError('Select a customer account first.');
      return;
    }

    if (!reason.trim()) {
      setError('Enter a support reason or ticket reference.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const response = await startSupportMasquerade({
        userId: selectedUser.id,
        reason: reason.trim(),
      });
      await onStarted(response.token, response.user);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>Masquerade as customer</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            select
            label="Team"
            value={teamId}
            onChange={(event) => {
              setTeamId(event.target.value);
              setSelectedUser(null);
            }}
            fullWidth
          >
            <MenuItem value="">All teams</MenuItem>
            {(data?.teams ?? []).map((team) => (
              <MenuItem key={team.id} value={team.id}>
                {team.name} ({team.code})
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            options={data?.users ?? []}
            value={selectedUser}
            loading={isLoading}
            onChange={(_, value) => setSelectedUser(value)}
            onInputChange={(_, value) => setSearch(value)}
            getOptionLabel={(option) => `${option.name} - ${option.email}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id} sx={{ display: 'block !important' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email} - {option.teamName ?? 'No team'} - {formatRole(option.role)}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label="User" placeholder="Search name, email, or team" />
            )}
          />
          <TextField
            label="Reason or ticket reference"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Support ticket, billing check, onboarding help"
            required
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleStart}
          disabled={!selectedUser || !reason.trim() || submitting}
        >
          Start masquerade
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function App() {
  const location = useLocation();
  if (location.pathname.replace(/\/+$/, '') === '/privacy') return <PublicPrivacyPage />;
  if (location.pathname.replace(/\/+$/, '') === '/terms') return <PublicTermsRedirect />;
  return <ApplicationRoutes />;
}

function ApplicationRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const supportSession = useAuthStore((state) => state.supportSession);
  const sessionKind = useAuthStore((state) => state.sessionKind);
  const returnToKiosk = useAuthStore((state) => state.returnToKiosk);
  const startMasqueradeSession = useAuthStore((state) => state.startMasqueradeSession);
  const restoreSupportSession = useAuthStore((state) => state.restoreSupportSession);
  const refreshSessionUser = useAuthStore((state) => state.refreshSessionUser);
  const clearSession = useAuthStore((state) => state.clearSession);
  const markKioskActivity = useAuthStore((state) => state.markKioskActivity);
  const [isBootstrapping, setIsBootstrapping] = useState(Boolean(token && !user));
  const [kioskIdleWarning, setKioskIdleWarning] = useState(false);
  const [masqueradeDialogOpen, setMasqueradeDialogOpen] = useState(false);
  const [dismissedGuidePath, setDismissedGuidePath] = useState<string | null>(null);
  const { data: currentTeam, isLoading: currentTeamLoading } = useSWR(
    token && user?.teamId ? currentTeamKey : null,
    fetchCurrentTeam,
  );
  const shouldFetchTeamEntitlements = Boolean(token && user?.teamId && !supportSession);
  const { data: currentTeamEntitlements, isLoading: entitlementsLoading } = useSWR(
    shouldFetchTeamEntitlements ? [currentTeamEntitlementsKey, user?.teamId] : null,
    fetchTeamEntitlements,
    {
      shouldRetryOnError: (error) => !axios.isAxiosError(error) || error.response?.status !== 403,
    },
  );
  const shouldFetchReleaseNote = Boolean(token && user && !supportSession);
  const { data: releaseNoteResponse, mutate: mutateReleaseNote } = useSWR(
    shouldFetchReleaseNote ? currentReleaseNoteKey : null,
    fetchCurrentReleaseNote,
  );
  const isMobileBillingReturn = location.pathname === '/mobile/billing-return';
  const kioskTokenExpiresAt = useMemo(
    () => (sessionKind === 'kiosk' && token ? getJwtExpiresAtMs(token) : null),
    [sessionKind, token],
  );
  const kioskTokenAlreadyExpired =
    sessionKind === 'kiosk' && kioskTokenExpiresAt !== null && kioskTokenExpiresAt <= Date.now();

  const finishKioskSession = useCallback(
    async (reason: KioskClientTerminationReason = 'USER_DONE') => {
      setKioskIdleWarning(false);
      const cleanup = clearSession(reason);
      navigate('/kiosk', { replace: true });
      await cleanup;
    },
    [clearSession, navigate],
  );

  useEffect(() => {
    if (!token || sessionKind !== 'kiosk') {
      setKioskIdleWarning(false);
      return;
    }

    const lastActivityAt = useAuthStore.getState().kioskLastActivityAt ?? Date.now();
    return startKioskAccountInactivityWatcher({
      lastActivityAt,
      expiresAt: kioskTokenExpiresAt,
      onActivity: markKioskActivity,
      onActive: () => setKioskIdleWarning(false),
      onWarning: () => setKioskIdleWarning(true),
      onTimeout: () =>
        void finishKioskSession(
          kioskTokenExpiresAt !== null && Date.now() >= kioskTokenExpiresAt
            ? 'TOKEN_EXPIRED'
            : 'INACTIVITY',
        ),
    });
  }, [finishKioskSession, kioskTokenExpiresAt, markKioskActivity, sessionKind, token]);

  useEffect(() => {
    if (kioskTokenAlreadyExpired) void finishKioskSession('TOKEN_EXPIRED');
  }, [finishKioskSession, kioskTokenAlreadyExpired]);

  useEffect(() => {
    if (isMobileBillingReturn) return;
    let isActive = true;

    if (!token) {
      setIsBootstrapping(false);
      return () => {
        isActive = false;
      };
    }

    setIsBootstrapping(!user);

    fetchCurrentUser()
      .then(async (currentUser) => {
        if (isActive) {
          await refreshSessionUser(token, currentUser);
        }
      })
      .catch((error) => {
        console.error('[Auth bootstrap]', getErrorMessage(error));
        if (isActive) {
          void clearSessionForExpectedToken(token);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsBootstrapping(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [clearSession, refreshSessionUser, token, isMobileBillingReturn]);

  const guide = workspaceGuide(currentTeamEntitlements);
  const guidePath = guide?.path ?? null;

  useEffect(() => {
    if (!user?.id || !user.teamId) {
      setDismissedGuidePath(null);
      return;
    }

    setDismissedGuidePath(readDismissedWorkspaceGuidePath(user.id, user.teamId));
  }, [user?.id, user?.teamId]);

  useEffect(() => {
    if (!user?.id || !user.teamId || guidePath) {
      return;
    }

    clearDismissedWorkspaceGuidePath(user.id, user.teamId);
    setDismissedGuidePath(null);
  }, [guidePath, user?.id, user?.teamId]);

  const dismissWorkspaceGuide = useCallback(
    (path: string) => {
      setDismissedGuidePath(path);
      if (user?.id && user.teamId) {
        writeDismissedWorkspaceGuidePath(user.id, user.teamId, path);
      }
    },
    [user?.id, user?.teamId],
  );

  const teamOptions = useMemo(
    () => (user?.teamId ? [{ id: user.teamId, name: currentTeam?.name ?? 'Current Team' }] : []),
    [currentTeam?.name, user?.teamId],
  );

  const userMenuItems = useMemo(() => {
    const items = user?.accountDisabled
      ? [...LOGOUT_ONLY_MENU_ITEMS]
      : [...DEFAULT_USER_MENU_ITEMS];
    if (supportSession) {
      items.push(RESTORE_SUPPORT_SESSION_MENU_ITEM);
    }
    if (user && hasSupportMasqueradeAccess(user)) {
      items.push(SUPPORT_MASQUERADE_MENU_ITEM);
    }
    if (!HELP_PAGES_ENABLED) {
      return items.filter((item) => item.id !== 'help');
    }
    return items;
  }, [supportSession, user?.accountDisabled, user?.email, user?.supportMasqueradeAllowed]);

  async function handleRestoreSupportSession() {
    await restoreSupportSession();
    navigate('/sessions', { replace: true });
  }

  function openSupportMasqueradeDialog() {
    blurActiveElement();
    setMasqueradeDialogOpen(true);
  }

  function handleLogout() {
    if (sessionKind === 'kiosk') {
      void finishKioskSession();
      return;
    }

    void clearSession().then(() => navigate('/login'));
  }

  async function handleDismissReleaseNotes(releaseKeys: string[]) {
    await Promise.all(releaseKeys.map((releaseKey) => dismissReleaseNote(releaseKey)));
    await mutateReleaseNote({ releaseNotes: [] }, { revalidate: false });
  }

  const masqueradeBanner = supportSession ? (
    <Alert severity="warning" sx={{ borderRadius: 0 }}>
      Masquerading as {user?.name}. App data is using this customer's account.
    </Alert>
  ) : null;

  const kioskSessionNotice =
    sessionKind === 'kiosk' ? (
      <Alert
        severity={kioskIdleWarning ? 'warning' : 'info'}
        variant="filled"
        sx={{ borderRadius: 0 }}
        action={
          <Button color="inherit" size="small" onClick={() => void finishKioskSession()}>
            Done / Sign out
          </Button>
        }
      >
        {kioskIdleWarning
          ? `No activity detected. This account will sign out in ${KIOSK_ACCOUNT_WARNING_MS / 1_000} seconds.`
          : `Shared kiosk session. Select Done / Sign out when finished. Automatically returns to the kiosk after ${KIOSK_ACCOUNT_IDLE_MS / 60_000} minutes of inactivity.`}
      </Alert>
    ) : null;

  const historicalSessionsOnly = isHistoricalSessionsOnlyAccess({
    user,
    entitlements: currentTeamEntitlements,
  });
  const guideOpen = Boolean(
    guidePath &&
    dismissedGuidePath !== guidePath &&
    location.pathname !== guidePath &&
    !location.pathname.startsWith(`${guidePath}/`),
  );
  const mainNavItems = useMemo(
    () => navItemsForWorkspace(user, currentTeamEntitlements),
    [currentTeamEntitlements, user],
  );

  const entry = selectEntryRoute({
    pathname: location.pathname, search: location.search, token, user,
    isBootstrapping, kioskTokenAlreadyExpired, returnToKiosk,
  });
  if (entry) return <EntryRoutes entry={entry} kioskSessionNotice={kioskSessionNotice} />;

  // Entry guards above establish the authenticated user before workspace gates.
  if (!user) return null;
  const workspaceGate = selectWorkspaceGate({
    user, currentTeam, currentTeamLoading, entitlements: currentTeamEntitlements, entitlementsLoading,
  });
  if (workspaceGate === 'loading') return <RouteLoading fullPage />;

  if (workspaceGate === 'setup') {
    return (
      <AuthenticatedShell
        navItems={withoutHelpNavItems(NO_TEAM_NAV_ITEMS)}
        fallbackPath="/setup-team"
        teams={[]}
        currentTeamId=""
        onTeamChange={() => undefined}
        user={user!}
        userMenuItems={userMenuItems}
        onSupportMasquerade={openSupportMasqueradeDialog}
        onRestoreSupportSession={handleRestoreSupportSession}
        onLogout={handleLogout}
        logoutLabel={sessionKind === 'kiosk' ? 'Done / Sign out' : undefined}
        sessionNotice={kioskSessionNotice}
      >
        {masqueradeBanner}
        {!supportSession ? <UsagePaymentPrompt /> : null}
        <WorkspaceRoutes mode="setup" user={user} currentTeam={currentTeam} />
        <SupportMasqueradeDialog
          open={masqueradeDialogOpen}
          onClose={() => setMasqueradeDialogOpen(false)}
          onStarted={startMasqueradeSession}
        />
      </AuthenticatedShell>
    );
  }

  if (workspaceGate === 'billing-recovery') {
    return (
      <AuthenticatedShell
        navItems={withoutHelpNavItems(BILLING_RECOVERY_NAV_ITEMS)}
        fallbackPath="/drivers"
        teams={teamOptions}
        currentTeamId={user.teamId ?? ''}
        onTeamChange={() => undefined}
        teamSubtitle={formatRole(user.role)}
        user={user}
        userMenuItems={userMenuItems}
        onSupportMasquerade={openSupportMasqueradeDialog}
        onRestoreSupportSession={handleRestoreSupportSession}
        onLogout={handleLogout}
        logoutLabel={sessionKind === 'kiosk' ? 'Done / Sign out' : undefined}
        sessionNotice={kioskSessionNotice}
      >
        {masqueradeBanner}
        <WorkspaceRoutes mode="billing-recovery" user={user} />
        <SupportMasqueradeDialog
          open={masqueradeDialogOpen}
          onClose={() => setMasqueradeDialogOpen(false)}
          onStarted={startMasqueradeSession}
        />
      </AuthenticatedShell>
    );
  }

  return (
    <CircuitDiscoveryProvider>
      <AuthenticatedShell
        navItems={mainNavItems}
        fallbackPath="/drivers"
        teams={teamOptions}
        currentTeamId={user.teamId ?? ''}
        onTeamChange={() => undefined}
        teamSubtitle={formatRole(user.role)}
        user={user}
        userMenuItems={
          historicalSessionsOnly
            ? userMenuItems.filter(
                (item) =>
                  item.id !== 'profile' && item.id !== 'measurements' && item.id !== 'password',
              )
            : userMenuItems
        }
        onSupportMasquerade={openSupportMasqueradeDialog}
        onRestoreSupportSession={handleRestoreSupportSession}
        onLogout={handleLogout}
        logoutLabel={sessionKind === 'kiosk' ? 'Done / Sign out' : undefined}
        sessionNotice={kioskSessionNotice}
      >
        {masqueradeBanner}
        {!supportSession ? <UsagePaymentPrompt /> : null}
        <WorkspaceRoutes mode="ready" user={user} historicalSessionsOnly={historicalSessionsOnly} />
        <Snackbar
          open={guideOpen}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={(_, reason) => {
            if (reason !== 'clickaway' && guidePath) {
              dismissWorkspaceGuide(guidePath);
            }
          }}
        >
          <Alert
            severity="info"
            variant="filled"
            onClose={() => {
              if (guidePath) {
                dismissWorkspaceGuide(guidePath);
              }
            }}
            action={
              guide ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => dismissWorkspaceGuide(guide.path)}
                  >
                    Dismiss
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      navigate(guide.path);
                      dismissWorkspaceGuide(guide.path);
                    }}
                  >
                    {guide.action}
                  </Button>
                </Stack>
              ) : null
            }
            sx={{ alignItems: 'center' }}
          >
            {guide?.message}
          </Alert>
        </Snackbar>
        <WhatsNewDialog
          releaseNotes={releaseNoteResponse?.releaseNotes ?? []}
          onDismiss={handleDismissReleaseNotes}
        />
        <OnboardingWizardDialog
          user={user}
          disabled={Boolean(supportSession || releaseNoteResponse?.releaseNotes.length)}
        />
        <SupportMasqueradeDialog
          open={masqueradeDialogOpen}
          onClose={() => setMasqueradeDialogOpen(false)}
          onStarted={startMasqueradeSession}
        />
      </AuthenticatedShell>
    </CircuitDiscoveryProvider>
  );
}
